import { FlagServiceError } from "./errors";
import type { EmilFlagsClientOptions, TokenProvider } from "./types";
import type {
  WireCapabilityDefinition,
  WireEvaluateAllFlagsResponse,
  WireEvaluateFlagResponse,
  WireEvaluationContext,
  WireFlagEvaluation,
  WireGetCapabilitiesResponse,
  WireGetCapabilityDefinitionsResponse,
} from "./wire";

const API_PREFIX = "/flagservice/v1";
const DEFAULT_TIMEOUT_MS = 10_000;

/**
 * Thin REST client over the flagservice EvaluationService. It speaks the
 * grpc-gateway JSON layer and knows nothing about OpenFeature — the
 * {@link EmilFlagsProvider} adapts its results to the OpenFeature contract.
 */
export class FlagServiceClient {
  private readonly baseUrl: string;
  private readonly environmentKey: string;
  private readonly clientSdkKey: string | undefined;
  private readonly tokenProvider: TokenProvider | undefined;
  private readonly fetchImpl: typeof fetch;
  private readonly timeoutMs: number;

  constructor(options: EmilFlagsClientOptions) {
    if (!options.clientSdkKey && !options.environmentKey) {
      throw new FlagServiceError(
        "either clientSdkKey or environmentKey is required",
      );
    }

    // An empty or omitted baseUrl means "same origin": requests go to
    // `/flagservice/v1/...` relative to the page, served by a dev proxy or a
    // reverse proxy. Only the trailing slash needs normalizing.
    this.baseUrl = (options.baseUrl ?? "").replace(/\/+$/, "");
    // With a clientSdkKey the server derives the environment from the key, so
    // the body's environmentKey is just a hint/fallback.
    this.environmentKey = options.environmentKey ?? "";
    this.clientSdkKey = options.clientSdkKey;
    this.tokenProvider = options.token;
    this.timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

    if (options.fetch) {
      this.fetchImpl = options.fetch;
    } else if (typeof globalThis.fetch === "function") {
      // Bind the global so browsers don't throw "Illegal invocation".
      this.fetchImpl = globalThis.fetch.bind(globalThis);
    } else {
      throw new FlagServiceError(
        "No global fetch available; pass a `fetch` implementation in the options",
      );
    }
  }

  /** Evaluate every flag in the environment for the given context. */
  async evaluateAll(context: WireEvaluationContext): Promise<WireFlagEvaluation[]> {
    const response = await this.post<WireEvaluateAllFlagsResponse>("/evaluate-all", {
      environmentKey: this.environmentKey,
      context,
    });
    return response.evaluations ?? [];
  }

  /** Evaluate a single flag for the given context. */
  async evaluate(
    flagKey: string,
    context: WireEvaluationContext,
  ): Promise<WireFlagEvaluation | undefined> {
    const response = await this.post<WireEvaluateFlagResponse>("/evaluate", {
      flagKey,
      environmentKey: this.environmentKey,
      context,
    });
    return response.evaluation;
  }

  /**
   * Resolve the caller's capability set for the configured environment. The
   * tenant is derived from the bearer token (never sent in the request); the
   * optional `context` enables per-user targeting and percentage rollouts.
   */
  async getCapabilities(
    context?: WireEvaluationContext,
  ): Promise<WireGetCapabilitiesResponse> {
    const query: Record<string, string> = {};
    if (this.environmentKey) {
      query.environmentKey = this.environmentKey;
    }
    if (context?.key) {
      query["context.key"] = context.key;
    }
    if (context?.attributes) {
      query["context.attributes"] = context.attributes;
    }
    return this.get<WireGetCapabilitiesResponse>("/capabilities", query);
  }

  /**
   * List the catalog of capability definitions the platform can expose. The set
   * is platform-wide (not environment- or tenant-specific), so it takes no
   * arguments.
   */
  async getCapabilityDefinitions(): Promise<WireCapabilityDefinition[]> {
    const response = await this.get<WireGetCapabilityDefinitionsResponse>(
      "/capabilities/definitions",
    );
    return response.definitions ?? [];
  }

  private post<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>("POST", path, { body });
  }

  private get<T>(path: string, query?: Record<string, string>): Promise<T> {
    return this.request<T>("GET", path, { query });
  }

  private async request<T>(
    method: "GET" | "POST",
    path: string,
    { body, query }: { body?: unknown; query?: Record<string, string> } = {},
  ): Promise<T> {
    // SDK-key auth: the server reads the `sdk_key` query param, derives the
    // environment from it, and skips the bearer/platform-admin chain. It's sent
    // as a query param rather than an X-SDK-Key header because our API gateway
    // strips unknown request headers. The key is a public client key (already
    // exposed in the browser), not a secret, so query-string transport is fine.
    const finalQuery = this.clientSdkKey
      ? { ...query, sdk_key: this.clientSdkKey }
      : query;
    const url = this.buildUrl(path, finalQuery);
    const headers: Record<string, string> = {
      accept: "application/json",
    };
    if (body !== undefined) {
      headers["content-type"] = "application/json";
    }

    const token = await this.resolveToken();
    if (token) {
      headers.authorization = `Bearer ${token}`;
    }

    const controller = new AbortController();
    const timer: ReturnType<typeof setTimeout> = setTimeout(
      () => controller.abort(),
      this.timeoutMs,
    );

    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: controller.signal,
        // The flagservice authenticates via the Authorization header (or the
        // sdk_key query param) — it never reads cookies. When the SDK is configured for a
        // same-origin base URL (the dev proxy, or a reverse proxy in
        // production), the browser would otherwise attach every cookie for that
        // origin to each request. That bloats the request headers — enough to
        // trip a proxy's max-header-size limit (e.g. Node's 8 KB default) and
        // get the request rejected with HTTP 431 before it reaches the
        // service — and needlessly leaks the host app's cookies to flagservice.
        // Omitting credentials keeps these requests cookie-free everywhere.
        credentials: "omit",
      });
    } catch (cause) {
      if (cause instanceof Error && cause.name === "AbortError") {
        throw new FlagServiceError(
          `request to ${path} timed out after ${this.timeoutMs}ms`,
          { cause },
        );
      }
      throw new FlagServiceError(`request to ${path} failed`, { cause });
    } finally {
      clearTimeout(timer);
    }

    if (!response.ok) {
      const detail = await response.text().catch(() => "");
      throw new FlagServiceError(
        `flagservice responded ${response.status} for ${path}${detail ? `: ${detail}` : ""}`,
        { status: response.status },
      );
    }

    return (await response.json()) as T;
  }

  /** `<baseUrl>/flagservice/v1<path>` with any query params appended. */
  private buildUrl(path: string, query?: Record<string, string>): string {
    const url = `${this.baseUrl}${API_PREFIX}${path}`;
    const qs = query ? new URLSearchParams(query).toString() : "";
    return qs ? `${url}?${qs}` : url;
  }

  private async resolveToken(): Promise<string | undefined> {
    const provider = this.tokenProvider;
    if (!provider) {
      return undefined;
    }
    const value = typeof provider === "function" ? await provider() : provider;
    return value ?? undefined;
  }
}
